'use client'

import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'
import {
  ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA,
  ABHA_HERO_SUCCESS_TITLE_MOBILE,
} from '@components/NewProduct/constants'
import { AbhaHeroCard } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroCard'
import { AbhaHeroCardCarousel } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroCardCarousel'
import { AbhaHeroSuccessContentSection } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessContentSection'
import { AbhaHeroSuccessActionButton } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessDownloadButton'
import { AbhaHeroSuccessRightPanel } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessRightPanel'
import type { AbhaHeroSuccessStepData } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessStep'
import { normalizeSuccessPanelData } from '@components/NewProduct/AbhaHeroWidget/steps/normalizeSuccessPanelData'
import { buildCardPreviewFromMember, type AbhaMemberListDetail } from '@helpers/abhaJourneyHelper'
import { getAbhaProfileInitials } from '@helpers/abhaWidgetHelper'
import { formatAbhaHeroStepTemplate } from '@components/NewProduct/AbhaHeroWidget/steps/abhaHeroStepData'
import type { AbhaSuccessAnalyticsVariant } from '@components/NewProduct/AbhaHeroWidget/abhaHeroAnalytics'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'

export type ExistingAbhaSuccessStepData = AbhaHeroSuccessStepData & {
  pageType?: string
  variantName?: AbhaSuccessAnalyticsVariant
  title?: string
  mobileTitle?: string
  description?: string
  viewCtaText?: string
  downloadCtaText?: string
}

export type AbhaHeroExistingAbhaSuccessStepProps = {
  data?: ExistingAbhaSuccessStepData
  phone: string
  members: AbhaMemberListDetail[]
  isMobile: boolean
  errorMessage?: string
  appDownloadLink: string
  onSelect: (member: AbhaMemberListDetail) => void
  onDownload: (member: AbhaMemberListDetail) => void
  onDownloadApp: () => void
  onInactiveCardClick?: () => void
  onViewAbha?: () => void
  ctaAnalyticsProps?: Record<string, unknown>
}

const getMemberKey = (member: AbhaMemberListDetail, index: number) =>
  `${String(member?.account_index ?? member?.abha_id ?? member?.id ?? 'member')}-${index}`

const formatMaskedOtpPhone = (maskedPhone: string | undefined, phone: string) => {
  const resolvedMaskedPhone = maskedPhone?.replace(/[()\s-]/g, '')

  if (resolvedMaskedPhone) {
    if (resolvedMaskedPhone.startsWith('+')) return resolvedMaskedPhone
    if (resolvedMaskedPhone.startsWith('91')) return `+${resolvedMaskedPhone}`

    return `+91${resolvedMaskedPhone}`
  }

  const sanitizedPhone = phone.replace(/\D/g, '').slice(-10)

  if (!sanitizedPhone) return ''

  return `+91${'*'.repeat(Math.max(sanitizedPhone.length - 1, 0))}${sanitizedPhone.slice(-1)}`
}

const ViewIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      d="M1.667 10s3.03-5 8.333-5 8.333 5 8.333 5-3.03 5-8.333 5-8.333-5-8.333-5Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <circle cx="10" cy="10" r="2.083" stroke="currentColor" strokeWidth="1.5" />
  </svg>
)

export const AbhaHeroExistingAbhaSuccessStep = ({
  data,
  phone,
  members,
  isMobile,
  errorMessage,
  appDownloadLink,
  onSelect,
  onDownload,
  onDownloadApp,
  onInactiveCardClick,
  onViewAbha,
  ctaAnalyticsProps,
}: AbhaHeroExistingAbhaSuccessStepProps) => {
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    setActiveIndex(currentIndex => Math.min(currentIndex, Math.max(members?.length - 1, 0)))
  }, [members?.length])

  const cardData = useMemo(() => members.map(buildCardPreviewFromMember), [members])
  const carouselItems = useMemo(
    () =>
      members.map((member, index) => ({
        id: getMemberKey(member, index),
        profileImageUrl: cardData[index]?.profileImageUrl,
        name: cardData[index]?.name,
        isCardFetched: Boolean(member?.token),
      })),
    [cardData, members]
  )
  const activeMember = members?.[activeIndex]
  const shouldShowOtpHelper = members.length > 1 && !activeMember?.token
  const maskedOtpPhone = formatMaskedOtpPhone(activeMember?.masked_phone_number, phone)
  const title = isMobile
    ? data?.mobileTitle ?? ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA.MOBILE_TITLE
    : data?.title ?? ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA.TITLE
  const description = formatAbhaHeroStepTemplate(
    data?.description ?? ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA.DESCRIPTION,
    { phone_number: phone }
  )
  const panelData = normalizeSuccessPanelData(data?.mobileData, {
    title: ABHA_HERO_SUCCESS_TITLE_MOBILE,
    appLink: appDownloadLink,
  })

  const handleActiveCardAction = () => {
    if (!activeMember) return

    if (activeMember?.token) {
      onDownload(activeMember)
      return
    }

    onViewAbha?.()
    onSelect(activeMember)
  }

  const renderCompactCard = (member: AbhaMemberListDetail, index: number) => {
    const memberCard = cardData[index]

    return (
      <button
        key={getMemberKey(member, index)}
        type="button"
        className="abha-hero-existing-success__list-card"
        onClick={() => {
          setActiveIndex(index)
          if (!member?.token) onSelect(member)
        }}>
        <PropertyControlledComponent
          controllerProperty={!!memberCard?.profileImageUrl}
          fallback={
            <span className="abha-hero-existing-success__list-avatar-fallback" aria-hidden>
              {getAbhaProfileInitials(memberCard?.name)}
            </span>
          }>
          <Image
            src={memberCard.profileImageUrl || ''}
            alt=""
            width={52}
            height={52}
            unoptimized={memberCard.profileImageUrl?.startsWith('data:')}
            className="abha-hero-existing-success__list-avatar"
            aria-hidden
          />
        </PropertyControlledComponent>
        <span className="abha-hero-existing-success__list-identity">
          <strong>{memberCard?.name}</strong>
          {memberCard?.dateOfBirth && <span>{memberCard.dateOfBirth}</span>}
        </span>
        <span className="abha-hero-existing-success__list-arrow" aria-hidden>
          ›
        </span>
      </button>
    )
  }

  if (!isMobile) {
    return (
      <div className="abha-hero-success abha-hero-success--desktop abha-hero-existing-success">
        <section className="abha-hero-existing-success__left">
          <div className="abha-hero-existing-success__left-content">
            <div className="abha-hero-existing-success__heading">
              <h2>{title}</h2>
              {description && members?.length > 1 && <p>{description}</p>}
              {errorMessage && <p className="abha-hero-existing-success__error">{errorMessage}</p>}
            </div>

            <div className="abha-hero-existing-success__desktop-list">
              <div className="abha-hero-existing-success__compact-list">
                {members.map((member, index) => {
                  const memberCard = cardData[index]

                  if (!member?.token || !memberCard) return renderCompactCard(member, index)

                  return (
                    <div key={getMemberKey(member, index)} className="abha-hero-existing-success__verified-panel">
                      <AbhaHeroCard
                        data={memberCard}
                        layout="resolve"
                        variant="showAbhaNumber"
                        animateReveal={false}
                        className="abha-hero-existing-success__verified-card"
                      />
                      <AbhaHeroSuccessActionButton
                        label={data?.downloadCtaText ?? ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA.DOWNLOAD_CTA_TEXT}
                        onClick={() => onDownload(member)}
                        fullWidth
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        <AbhaHeroSuccessRightPanel
          data={data}
          appDownloadLink={appDownloadLink}
          ctaAnalyticsProps={ctaAnalyticsProps}
        />
      </div>
    )
  }

  return (
    <div className="abha-hero-existing-success abha-hero-existing-success--mobile">
      <section className="abha-hero-existing-success__mobile-top">
        <div className="abha-hero-existing-success__mobile-heading">
          <h2>{title}</h2>
          {shouldShowOtpHelper && maskedOtpPhone && members?.length > 1 && (
            <p className="abha-hero-existing-success__otp-helper">
              {/* To download the cards, enter the OTP sent to ({maskedOtpPhone}) */}
              We will send an OTP to ({maskedOtpPhone}) to confirm your identity before you can download the card
            </p>
          )}
        </div>
        {errorMessage && <p className="abha-hero-existing-success__error">{errorMessage}</p>}

        <AbhaHeroCardCarousel
          items={carouselItems}
          className="abha-hero-existing-success__card-carousel"
          onActiveIndexChange={(index, source) => {
            if (source === 'avatar') onInactiveCardClick?.()
            setActiveIndex(index)
          }}
          renderCard={index => (
            <AbhaHeroCard
              data={cardData[index]}
              layout="resolve"
              variant="showAbhaNumber"
              animateReveal={false}
              className="abha-hero-existing-success__mobile-card"
            />
          )}
        />

        <button type="button" className="abha-hero-existing-success__card-action" onClick={handleActiveCardAction}>
          {!activeMember?.token && <ViewIcon />}
          <span>
            {activeMember?.token
              ? data?.downloadCtaText ?? ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA.DOWNLOAD_CTA_TEXT
              : data?.viewCtaText ?? ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA.VIEW_CTA_TEXT}
          </span>
        </button>
      </section>

      {!shouldShowOtpHelper &&
        (panelData.header.title || panelData.header.description || panelData.content.length > 0) && (
          <section
            className={`abha-hero-existing-success__mobile-bottom abha-hero-success-panel--${panelData.panelVariant}`}>
            {(panelData.header.title || panelData.header.description) && (
              <div className="abha-hero-success__mobile-show-bottom-header">
                {panelData.header.title && (
                  <h2 className="abha-hero-success__mobile-show-bottom-title">{panelData.header.title}</h2>
                )}
                {panelData.header.description && (
                  <p className="abha-hero-success__mobile-show-bottom-description">{panelData.header.description}</p>
                )}
              </div>
            )}
            <AbhaHeroSuccessContentSection
              panelData={panelData}
              appDownloadLink={appDownloadLink}
              isMobile
              onDownloadApp={onDownloadApp}
              ctaAnalyticsProps={ctaAnalyticsProps}
            />
          </section>
        )}
    </div>
  )
}
